import { useState, useRef, useEffect } from "react";
import { Share2, Check, MessageCircle, Instagram, Copy, Link as LinkIcon, X } from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";

const isMobile = () =>
    typeof navigator !== "undefined" && /Mobi|Android|iPhone|iPad/i.test(navigator.userAgent || "");

/**
 * Multi-channel share popover.
 *
 * Channels (in order of priority on desktop):
 *   1. WhatsApp  → wa.me link with pre-filled text
 *   2. Copy link → clipboard
 *   3. Instagram Story → Web Share API (mobile only) or fallback OG-image download flow
 *   4. Native share (mobile) → navigator.share
 *
 * On mobile, the native share sheet is offered as the primary entry point
 * (browser surfaces all installed apps including Instagram & WhatsApp), but
 * the explicit WhatsApp / IG / Copy buttons are still visible underneath so
 * users can pick a channel without opening the sheet.
 */
export default function ShareButton({
    recId,
    basedOn,
    label,
    url: urlOverride,
    text: textOverride,
    ogImageUrl,
    testIdPrefix = "share",
}) {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const [copied, setCopied] = useState(false);
    const popoverRef = useRef(null);

    const url = urlOverride || `${window.location.origin}/r/${recId}`;
    const text = textOverride || t("share.default_text", { basedOn });
    const fullText = `${text} · ${url}`;
    const onMobile = isMobile();

    useEffect(() => {
        const handler = (e) => {
            if (popoverRef.current && !popoverRef.current.contains(e.target)) {
                setOpen(false);
            }
        };
        if (open) document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, [open]);

    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(url);
            setCopied(true);
            toast.success(t("share.copied_toast"));
            setTimeout(() => setCopied(false), 1800);
        } catch {
            toast.error(t("share.copy_failed"));
        }
    };

    const handleWhatsApp = () => {
        const wa = `https://wa.me/?text=${encodeURIComponent(fullText)}`;
        window.open(wa, "_blank", "noopener,noreferrer");
    };

    const handleNativeShare = async () => {
        if (!navigator.share) return;
        try {
            await navigator.share({ title: "RecomendaLibros", text, url });
        } catch { /* user cancelled */ }
    };

    /**
     * Try to share an image FILE via the Web Share API. iOS Safari ≥16 and
     * Android Chrome support `navigator.share({files})` which lets the user
     * pick "Instagram → Stories" and lands directly on the camera screen with
     * the image pre-loaded — no manual download / clipboard step.
     *
     * Returns true on success, false if files-share is unsupported (caller
     * should fall back to the link-only flow).
     */
    const _shareImageFile = async (imageUrl) => {
        if (!imageUrl || !navigator.canShare || !navigator.share) return false;
        try {
            const resp = await fetch(imageUrl);
            if (!resp.ok) return false;
            const blob = await resp.blob();
            const file = new File([blob], `recomendalibros-${recId || "share"}.jpg`, {
                type: blob.type || "image/jpeg",
            });
            if (!navigator.canShare({ files: [file] })) return false;
            // We deliberately omit `url` here: when sharing to Instagram
            // Stories, including a URL forces the "Sticker" mode; sharing
            // the file alone opens the regular Story creation flow.
            await navigator.share({ title: "RecomendaLibros", text, files: [file] });
            return true;
        } catch (err) {
            // AbortError = user cancelled. Treat as "handled" so we don't
            // also trigger the fallback download.
            if (err && err.name === "AbortError") return true;
            return false;
        }
    };

    const handleInstagramStory = async () => {
        // Mobile path: try files-share first (lands in Instagram Stories with
        // image ready). Fall back to link-share if files-share is unsupported.
        if (onMobile) {
            const ok = await _shareImageFile(ogImageUrl);
            if (ok) return;
            if (navigator.share) {
                try {
                    await navigator.share({ title: "RecomendaLibros", text, url });
                } catch { /* cancelled */ }
                return;
            }
        }
        // Desktop fallback: copy the link + download the OG image so the
        // user can drag it into Instagram on their phone or DM it.
        try {
            await navigator.clipboard.writeText(url);
            toast.success(t("share.ig_desktop_hint"));
        } catch {
            toast.error(t("share.copy_failed"));
        }
        if (ogImageUrl) {
            const a = document.createElement("a");
            a.href = ogImageUrl;
            a.download = `recomendalibros-${recId || "share"}.jpg`;
            a.target = "_blank";
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
        }
    };

    return (
        <div className="relative inline-block" ref={popoverRef}>
            <button
                type="button"
                data-testid={`${testIdPrefix}-btn-${recId || "x"}`}
                onClick={() => setOpen((o) => !o)}
                className="inline-flex items-center gap-2 border border-[var(--ink)] text-[var(--ink)] bg-transparent rounded-full hover:bg-[var(--ink)] hover:text-[var(--bg)] transition-colors px-5 py-2 text-xs uppercase tracking-widest"
            >
                <Share2 size={14} strokeWidth={1.5} />
                <span>{label || t("share.button")}</span>
            </button>

            {open && (
                <>
                    {/* Mobile-only backdrop. Tappable to dismiss. */}
                    <div
                        className="fixed inset-0 bg-black/50 z-40 md:hidden"
                        onClick={() => setOpen(false)}
                        aria-hidden="true"
                    />
                    <div
                        role="menu"
                        data-testid={`${testIdPrefix}-popover`}
                        className="
                            fixed left-0 right-0 bottom-0 z-50 pb-4 pt-2
                            md:absolute md:left-auto md:right-0 md:top-full md:bottom-auto md:mt-2 md:pb-2 md:pt-2
                            bg-[var(--bg-elevated)] border border-[var(--border-strong)]
                            shadow-2xl md:min-w-[260px] md:shadow-xl
                            md:max-h-[min(420px,calc(100vh-120px))] overflow-y-auto
                        "
                    >
                        <div className="flex items-center justify-between px-4 md:px-3 py-2 md:py-1 border-b border-[var(--border)]">
                            <span className="text-[10px] uppercase tracking-[0.3em] text-[var(--ink-muted)]">
                                {t("share.share_via")}
                            </span>
                            <button
                                type="button"
                                onClick={() => setOpen(false)}
                                className="text-[var(--ink-dim)] hover:text-[var(--ink)] p-1"
                                aria-label="Close"
                            >
                                <X size={14} strokeWidth={1.8} />
                            </button>
                        </div>

                        <ChannelButton
                            icon={MessageCircle}
                            label={t("share.whatsapp")}
                            onClick={() => { handleWhatsApp(); setOpen(false); }}
                            testId={`${testIdPrefix}-whatsapp`}
                            accent
                        />
                        <ChannelButton
                            icon={copied ? Check : Copy}
                            label={copied ? t("share.copied") : t("share.copy_link")}
                            onClick={() => { handleCopy(); }}
                            testId={`${testIdPrefix}-copy`}
                        />
                        <ChannelButton
                            icon={Instagram}
                            label={t("share.instagram")}
                            onClick={() => { handleInstagramStory(); setOpen(false); }}
                            testId={`${testIdPrefix}-instagram`}
                            hint={!onMobile ? t("share.ig_desktop_hint_short") : null}
                        />
                        {onMobile && navigator.share && (
                            <ChannelButton
                                icon={LinkIcon}
                                label={t("share.more_options")}
                                onClick={() => { handleNativeShare(); setOpen(false); }}
                                testId={`${testIdPrefix}-native`}
                            />
                        )}
                    </div>
                </>
            )}
        </div>
    );
}

function ChannelButton({ icon: Icon, label, onClick, testId, accent, hint }) {
    return (
        <button
            type="button"
            data-testid={testId}
            onClick={onClick}
            className="w-full flex items-center gap-3 px-4 py-2.5 text-left text-sm text-[var(--ink)] hover:bg-[var(--bg-hover)] transition-colors"
        >
            <Icon size={14} strokeWidth={1.5} className={accent ? "text-[var(--accent)]" : "text-[var(--ink-muted)]"} />
            <span className="flex-1">{label}</span>
            {hint && <span className="text-[10px] text-[var(--ink-dim)] uppercase tracking-widest">{hint}</span>}
        </button>
    );
}
